/* Casa de Pastel — BookThatApp bridge
 *
 * Fetches availability from the existing BTA install via the public JSONP
 * endpoint and disables matching dates on native <input type="date"> fields.
 *
 * Public API:
 *   window.CDPBTA.applyToInput(inputEl, { productId, months, onChange, hintEl })
 *   window.CDPBTA.loadDisabledDates({ productId, months }) -> Promise<Set<string>>
 *
 * Disabled-date set contains 'YYYY-MM-DD' strings in the shop's local time.
 */
(function () {
  var SHOP = 'casa-de-pastel.bookthatapp.com';
  var CACHE_TTL_MS = 5 * 60 * 1000;
  var cache = {}; // key -> { ts, disabled: Set<string>, raw: object }

  function pad(n) { return n < 10 ? '0' + n : '' + n; }
  function isoFromParts(y, m, d) { return y + '-' + pad(m) + '-' + pad(d); }
  function todayLocalIso() {
    var t = new Date();
    return isoFromParts(t.getFullYear(), t.getMonth() + 1, t.getDate());
  }
  function addMonthsIso(months) {
    var t = new Date();
    t.setMonth(t.getMonth() + months);
    return isoFromParts(t.getFullYear(), t.getMonth() + 1, t.getDate());
  }
  // Iterate every YYYY-MM-DD between two BTA timestamp arrays [Y,M,D,...] (M 1-indexed).
  function eachDayInRange(startArr, endArr, cb) {
    var s = new Date(startArr[0], startArr[1] - 1, startArr[2]);
    var e = new Date(endArr[0], endArr[1] - 1, endArr[2]);
    while (s <= e) {
      cb(isoFromParts(s.getFullYear(), s.getMonth() + 1, s.getDate()));
      s.setDate(s.getDate() + 1);
    }
  }

  function jsonp(url) {
    return new Promise(function (resolve, reject) {
      var cbName = 'cdpBtaCb_' + Math.random().toString(36).slice(2) + '_' + Date.now();
      var s = document.createElement('script');
      var t = setTimeout(function () { cleanup(); reject(new Error('BTA timeout')); }, 8000);
      function cleanup() {
        clearTimeout(t);
        try { delete window[cbName]; } catch (e) { window[cbName] = undefined; }
        if (s.parentNode) s.parentNode.removeChild(s);
      }
      window[cbName] = function (data) { cleanup(); resolve(data); };
      s.onerror = function () { cleanup(); reject(new Error('BTA load error')); };
      var sep = url.indexOf('?') === -1 ? '?' : '&';
      s.src = url + sep + 'callback=' + cbName + '&_=' + Date.now();
      document.head.appendChild(s);
    });
  }

  // Compute the disabled-date Set for a given response payload + optional productId.
  function computeDisabled(data, productId) {
    var disabled = new Set();

    // 1. Blackouts that apply (scope 0 = all products; otherwise must list our product).
    (data.blackouts || []).forEach(function (b) {
      if (!b.all_day) return; // ignore partial-hour blackouts
      var applies = false;
      if (b.scope === 0 || !b.products || b.products.length === 0) {
        applies = true;
      } else if (productId) {
        applies = b.products.some(function (p) { return String(p) === String(productId); });
      }
      if (applies && b.start && b.end) {
        eachDayInRange(b.start, b.end, function (iso) { disabled.add(iso); });
      }
    });

    // 2. Per-product capacity exceeded + closed weekdays.
    //    Only when we have a productId — wizard (no productId) uses blackouts only.
    if (!productId) return disabled;
    (data.products || []).forEach(function (p) {
      if (String(p.id) !== String(productId)) return;
      var capacity = typeof p.capacity === 'number' ? p.capacity : null;

      // Closed weekdays (e.g., Sundays).
      var closedWeekdays = new Set();
      var seasons = (p.opening_hours && p.opening_hours.seasons) || [];
      seasons.forEach(function (season) {
        (season.days || []).forEach(function (d) {
          if (!d.hours || d.hours.length === 0) closedWeekdays.add(d.day);
        });
      });

      // Bookings: aggregate quantity per day.
      var perDay = {};
      (p.bookings || []).forEach(function (bk) {
        if (!bk.start) return;
        var iso = isoFromParts(bk.start[0], bk.start[1], bk.start[2]);
        perDay[iso] = (perDay[iso] || 0) + (bk.quantity || 1);
      });
      if (capacity) {
        Object.keys(perDay).forEach(function (iso) {
          if (perDay[iso] >= capacity) disabled.add(iso);
        });
      }

      // Mark closed weekdays for the requested window (if we have a meaningful range).
      if (closedWeekdays.size > 0) {
        var start = new Date();
        var end = new Date();
        end.setMonth(end.getMonth() + 6);
        var cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        while (cur <= end) {
          if (closedWeekdays.has(cur.getDay())) {
            disabled.add(isoFromParts(cur.getFullYear(), cur.getMonth() + 1, cur.getDate()));
          }
          cur.setDate(cur.getDate() + 1);
        }
      }
    });

    return disabled;
  }

  function loadDisabledDates(opts) {
    opts = opts || {};
    var months = opts.months || 6;
    var productId = opts.productId || null;
    var key = (productId || 'global') + ':' + months;
    var now = Date.now();
    if (cache[key] && (now - cache[key].ts) < CACHE_TTL_MS) {
      return Promise.resolve(cache[key].disabled);
    }

    var start = todayLocalIso();
    var end = addMonthsIso(months);
    var url = 'https://' + SHOP + '/availability?format=json&start=' + start + '&end=' + end;
    if (productId) url += '&products=' + encodeURIComponent(productId);

    return jsonp(url).then(function (data) {
      var disabled = computeDisabled(data, productId);
      cache[key] = { ts: Date.now(), disabled: disabled, raw: data };
      try {
        document.dispatchEvent(new CustomEvent('cdp-bta-loaded', {
          detail: { productId: productId, disabled: Array.from(disabled) }
        }));
      } catch (e) {}
      return disabled;
    }).catch(function (err) {
      console.warn('[CDPBTA] availability fetch failed:', err);
      return new Set();
    });
  }

  function whenFlatpickrReady(cb, attempts) {
    if (window.flatpickr) return cb();
    if ((attempts || 0) > 80) return cb(); // ~16s; give up and fall back
    setTimeout(function () { whenFlatpickrReady(cb, (attempts || 0) + 1); }, 200);
  }

  // Replace a native <input type="date"> with a flatpickr calendar that visually
  // greys out unavailable dates (blackouts, fully-booked, closed weekdays).
  function applyFlatpickr(inputEl, opts) {
    if (!inputEl) return;
    opts = opts || {};
    var hintEl = opts.hintEl || null;

    loadDisabledDates({ productId: opts.productId, months: opts.months }).then(function (disabled) {
      whenFlatpickrReady(function () {
        // Fall back to plain validation if flatpickr never loaded.
        if (!window.flatpickr) {
          inputEl.addEventListener('change', function () {
            if (inputEl.value && disabled.has(inputEl.value)) {
              if (hintEl) hintEl.style.color = '#ed4939';
              inputEl.value = '';
              inputEl.dispatchEvent(new Event('input', { bubbles: true }));
            }
          });
          return;
        }

        // Build the disable array: ISO strings + extra predicates (e.g. Sundays).
        var disableArr = Array.from(disabled);
        if (opts.disableSundays) {
          disableArr.push(function (date) { return date.getDay() === 0; });
        }
        if (typeof opts.extraDisable === 'function') {
          disableArr.push(opts.extraDisable);
        }

        var locale = opts.locale || (document.documentElement.lang || '').slice(0, 2);
        if (locale === 'es' && window.flatpickr.l10ns && window.flatpickr.l10ns.es) {
          // ok
        } else {
          locale = 'default';
        }

        // Minimum date: respect existing min attr (e.g. today+7 for workshops).
        var minDate = inputEl.getAttribute('min') || 'today';

        // Switch input type to text so flatpickr controls rendering on all browsers.
        if (inputEl.type === 'date') inputEl.type = 'text';
        inputEl.setAttribute('readonly', 'readonly');
        inputEl.setAttribute('placeholder', opts.placeholder || 'YYYY-MM-DD');

        var fp = window.flatpickr(inputEl, {
          dateFormat: 'Y-m-d',
          minDate: minDate,
          disable: disableArr,
          locale: locale,
          allowInput: false,
          disableMobile: true, // ensure flatpickr UI on mobile (not native picker)
          onChange: function (selectedDates, dateStr) {
            inputEl.value = dateStr;
            inputEl.dispatchEvent(new Event('input', { bubbles: true }));
            inputEl.dispatchEvent(new Event('change', { bubbles: true }));
            if (typeof opts.onChange === 'function') opts.onChange(dateStr);
          }
        });

        // Expose for debugging.
        inputEl._cdpFp = fp;
      });
    });
  }

  // Legacy plain-input mode (kept for back-compat, reject on change).
  function applyToInput(inputEl, opts) {
    if (!inputEl) return;
    opts = opts || {};
    var hintEl = opts.hintEl || null;
    var defaultHint = hintEl ? hintEl.textContent : '';
    var unavailableMsg = opts.unavailableMsg ||
      'Sorry, that date is not available. Please pick another day.';

    loadDisabledDates({ productId: opts.productId, months: opts.months }).then(function (disabled) {
      function check() {
        if (!inputEl.value) return;
        if (disabled.has(inputEl.value)) {
          if (hintEl) { hintEl.textContent = unavailableMsg; hintEl.style.color = '#ed4939'; }
          inputEl.setCustomValidity(unavailableMsg);
          var rejected = inputEl.value;
          inputEl.value = '';
          if (typeof opts.onReject === 'function') opts.onReject(rejected);
          inputEl.dispatchEvent(new Event('input', { bubbles: true }));
          inputEl.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          if (hintEl) { hintEl.textContent = defaultHint; hintEl.style.color = ''; }
          inputEl.setCustomValidity('');
        }
      }
      inputEl.addEventListener('change', check);
      inputEl.addEventListener('input', check);
      check();
    });
  }

  window.CDPBTA = {
    shop: SHOP,
    loadDisabledDates: loadDisabledDates,
    applyToInput: applyToInput,
    applyFlatpickr: applyFlatpickr
  };
})();
